/**
 * Google Maps 3D shape rendering.
 * Manages Polygon3DElement and Polyline3DElement for TAK shapes.
 * Circles are approximated as 64-vertex polygons.
 */

import type { TakShape, GeoPoint } from '@vtak/shared';
import { argbIntToHex, argbIntToHex8, circleToPolygonPoints } from '@vtak/shared';
import { CIRCLE_POLYGON_SEGMENTS } from '@/constants/drawing';
import type { ShapeDiff } from './shape-state';

/** Active 3D polygons keyed by shape UID */
const polygonMap3d = new Map<string, google.maps.maps3d.Polygon3DElement>();

/** Active 3D polylines keyed by shape UID */
const polylineMap3d = new Map<string, google.maps.maps3d.Polyline3DElement>();

/** Active map reference for cleanup */
let activeMap3d: google.maps.maps3d.Map3DElement | null = null;

/** Initialize the 3D shape renderer */
export function initShapes3D(): void {
  // No-op
}

/** Apply a shape diff to the 3D map */
export function applyShapeDiff3D(
  map3d: google.maps.maps3d.Map3DElement,
  diff: ShapeDiff,
): void {
  activeMap3d = map3d;

  for (const uid of diff.removed) {
    removeShape3D(uid, map3d);
  }

  for (const shape of diff.updated) {
    removeShape3D(shape.uid, map3d);
    addShape3D(map3d, shape);
  }

  for (const shape of diff.added) {
    addShape3D(map3d, shape);
  }
}

/** Destroy all 3D shapes */
export function destroyShapes3D(): void {
  if (!activeMap3d) return;

  for (const poly of polygonMap3d.values()) {
    try { activeMap3d.removeChild(poly); } catch { /* already removed */ }
  }
  for (const line of polylineMap3d.values()) {
    try { activeMap3d.removeChild(line); } catch { /* already removed */ }
  }

  polygonMap3d.clear();
  polylineMap3d.clear();
  activeMap3d = null;
}

/** Add a shape to the 3D map */
function addShape3D(map3d: google.maps.maps3d.Map3DElement, shape: TakShape): void {
  const strokeColor = argbIntToHex(shape.strokeColor);
  const fillColor = argbIntToHex8(shape.fillColor);

  switch (shape.shapeType) {
    case 'circle':
    case 'geofence':
      addCirclePolygon3D(map3d, shape, strokeColor, fillColor);
      break;
    case 'route':
    case 'freeform':
    case 'measurement':
      addPolyline3D(map3d, shape, strokeColor);
      break;
    case 'polygon':
    case 'rectangle':
      addPolygon3D(map3d, shape, strokeColor, fillColor);
      break;
  }
}

/** Add a circle as a polygon approximation in 3D */
function addCirclePolygon3D(
  map3d: google.maps.maps3d.Map3DElement,
  shape: TakShape & { center: GeoPoint; radiusMeters: number },
  strokeColor: string,
  fillColor: string,
): void {
  const points = circleToPolygonPoints(shape.center, shape.radiusMeters, CIRCLE_POLYGON_SEGMENTS);
  const coords = pointsToCoords(points);

  const polygon = new google.maps.maps3d.Polygon3DElement({
    outerCoordinates: coords,
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor,
    strokeWidth: shape.strokeWeight,
    fillColor,
    drawsOccludedSegments: true,
  });

  polygonMap3d.set(shape.uid, polygon);
  map3d.appendChild(polygon);
}

/** Add a closed polygon in 3D */
function addPolygon3D(
  map3d: google.maps.maps3d.Map3DElement,
  shape: TakShape & { vertices: GeoPoint[] },
  strokeColor: string,
  fillColor: string,
): void {
  const coords = pointsToCoords(shape.vertices);

  const polygon = new google.maps.maps3d.Polygon3DElement({
    outerCoordinates: coords,
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor,
    strokeWidth: shape.strokeWeight,
    fillColor,
    drawsOccludedSegments: true,
  });

  polygonMap3d.set(shape.uid, polygon);
  map3d.appendChild(polygon);
}

/** Add a polyline in 3D */
function addPolyline3D(
  map3d: google.maps.maps3d.Map3DElement,
  shape: TakShape,
  strokeColor: string,
): void {
  const path = extractPolylinePath(shape);
  const coords = pointsToCoords(path);

  const polyline = new google.maps.maps3d.Polyline3DElement({
    coordinates: coords,
    altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
    strokeColor,
    strokeWidth: shape.strokeWeight,
    drawsOccludedSegments: true,
  });

  polylineMap3d.set(shape.uid, polyline);
  map3d.appendChild(polyline);
}

/** Remove a shape from the 3D map */
function removeShape3D(uid: string, map3d: google.maps.maps3d.Map3DElement): void {
  const polygon = polygonMap3d.get(uid);
  if (polygon) {
    try { map3d.removeChild(polygon); } catch { /* already removed */ }
    polygonMap3d.delete(uid);
  }

  const polyline = polylineMap3d.get(uid);
  if (polyline) {
    try { map3d.removeChild(polyline); } catch { /* already removed */ }
    polylineMap3d.delete(uid);
  }
}

/** Extract polyline path from a shape */
function extractPolylinePath(shape: TakShape): GeoPoint[] {
  if ('vertices' in shape) return shape.vertices;
  if ('waypoints' in shape) return shape.waypoints.map((wp) => wp.position);
  if ('origin' in shape) return [shape.origin];
  return [];
}

/** Convert GeoPoint array to LatLngAltitudeLiteral array */
function pointsToCoords(points: GeoPoint[]): google.maps.LatLngAltitudeLiteral[] {
  return points.map((p) => ({ lat: p.lat, lng: p.lon, altitude: 0 }));
}
